import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  KeyRound, Users, Building2, Shield, Database, Activity, 
  Plus, Trash2, Ban, CheckCircle, Download, Upload, RefreshCw,
  Settings, BarChart3, Eye, EyeOff, ArrowLeft, Menu, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MasterControl() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [passkeys, setPasskeys] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // New passkey form
  const [newPasskey, setNewPasskey] = useState('');
  const [passkeyDesc, setPasskeyDesc] = useState('');
  const [passkeyFeatures, setPasskeyFeatures] = useState({
    costing: true,
    rfq: true,
    customers: true,
    reports: true
  });

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
  });

  // Helper to handle API responses and auto-logout on 403 - IMMEDIATE
  const handleApiResponse = async (response) => {
    if (response.status === 401 || response.status === 403) {
      const error = await response.json().catch(() => ({ detail: 'Access denied' }));
      // Immediate logout for any auth failure
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      sessionStorage.setItem('logout_reason', error.detail || 'Session ended');
      window.location.replace('/login'); // Use replace to prevent back button
      throw new Error(error.detail || 'Access denied');
    }
    return response;
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/master/stats`, { headers: getAuthHeaders() });
      await handleApiResponse(response);
      if (response.ok) setStats(await response.json());
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchPasskeys = async () => {
    try {
      const response = await fetch(`${API_URL}/api/master/passkeys`, { headers: getAuthHeaders() });
      await handleApiResponse(response);
      if (response.ok) {
        const data = await response.json();
        setPasskeys(data.passkeys || []);
      }
    } catch (error) {
      console.error('Failed to fetch passkeys:', error);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await fetch(`${API_URL}/api/master/tenants`, { headers: getAuthHeaders() });
      await handleApiResponse(response);
      if (response.ok) {
        const data = await response.json();
        setTenants(data.tenants || []);
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/master/users`, { headers: getAuthHeaders() });
      await handleApiResponse(response);
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/master/activity-logs?limit=50`, { headers: getAuthHeaders() });
      await handleApiResponse(response);
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchPasskeys();
    fetchTenants();
    fetchUsers();
    fetchLogs();
  }, []);

  const generatePasskey = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let key = '';
    for (let i = 0; i < 12; i++) {
      if (i > 0 && i % 4 === 0) key += '-';
      key += chars[Math.floor(Math.random() * chars.length)];
    }
    setNewPasskey(key);
  };

  const createPasskey = async () => {
    if (!newPasskey) {
      toast.error('Please generate or enter a passkey');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/master/passkeys`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          passkey: newPasskey, 
          description: passkeyDesc,
          features: passkeyFeatures
        })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Passkey created!');
        setNewPasskey('');
        setPasskeyDesc('');
        setPasskeyFeatures({ costing: true, rfq: true, customers: true, reports: true });
        fetchPasskeys();
      } else {
        toast.error(data.detail || 'Failed to create passkey');
      }
    } catch (error) {
      toast.error('Failed to create passkey');
    }
    setLoading(false);
  };

  const togglePasskey = async (passkey, currentStatus) => {
    const action = currentStatus === 'active' ? 'disable' : 'enable';
    try {
      const response = await fetch(`${API_URL}/api/master/passkeys/${passkey}/${action}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success(`Passkey ${action}d`);
        fetchPasskeys();
        fetchUsers();
      }
    } catch (error) {
      toast.error(`Failed to ${action} passkey`);
    }
  };

  const deletePasskey = async (passkey) => {
    if (!confirm('Are you sure you want to delete this passkey?')) return;
    try {
      const response = await fetch(`${API_URL}/api/master/passkeys/${passkey}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Passkey deleted');
        fetchPasskeys();
      }
    } catch (error) {
      toast.error('Failed to delete passkey');
    }
  };

  const toggleTenant = async (companyCode, currentStatus) => {
    const action = currentStatus === 'active' ? 'disable' : 'enable';
    try {
      const response = await fetch(`${API_URL}/api/master/tenants/${companyCode}/${action}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success(`Tenant ${action}d`);
        fetchTenants();
        fetchUsers();
      }
    } catch (error) {
      toast.error(`Failed to ${action} tenant`);
    }
  };

  const downloadBackup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/master/backup`, { headers: getAuthHeaders() });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rfq_backup_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        toast.success('Backup downloaded');
      }
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!confirm('This will restore data from backup. Are you sure?')) {
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/master/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Backup restored successfully');
        fetchStats();
      } else {
        toast.error(data.detail || 'Restore failed');
      }
    } catch (error) {
      toast.error('Failed to restore backup');
    }
    e.target.value = '';
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'passkeys', label: 'Passkeys', icon: KeyRound },
    { id: 'tenants', label: 'Tenants', icon: Building2 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'backup', label: 'Backup', icon: Database },
    { id: 'logs', label: 'Activity', icon: Activity },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white p-3 sm:p-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Shield className="w-6 h-6 sm:w-8 sm:h-8" />
            <div>
              <h1 className="text-base sm:text-xl font-bold">MASTER CONTROL</h1>
              <p className="text-[10px] sm:text-xs text-purple-200 hidden sm:block">System Administration Panel</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/dashboard'} 
            className="text-white border-white hover:bg-white/20 text-xs sm:text-sm px-2 sm:px-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Exit to Dashboard</span>
            <span className="sm:hidden">Exit</span>
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-3 sm:p-4 lg:p-6">
        {/* Mobile Tab Menu Button */}
        <div className="lg:hidden mb-4">
          <Button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full justify-between bg-white text-gray-700 border hover:bg-gray-50"
          >
            <span className="flex items-center gap-2">
              {tabs.find(t => t.id === activeTab)?.icon && 
                <span>{React.createElement(tabs.find(t => t.id === activeTab).icon, { className: "w-4 h-4" })}</span>
              }
              {tabs.find(t => t.id === activeTab)?.label}
            </span>
            <Menu className="w-4 h-4" />
          </Button>
          
          {mobileMenuOpen && (
            <div className="absolute left-3 right-3 mt-1 bg-white rounded-lg shadow-lg border z-40">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b last:border-b-0 ${
                    activeTab === tab.id ? 'bg-purple-50 text-purple-600' : 'text-gray-600'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Desktop Tabs */}
        <div className="hidden lg:flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-purple-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
            {[
              { label: 'Total Tenants', value: stats.total_tenants, color: 'blue' },
              { label: 'Active Tenants', value: stats.active_tenants, color: 'green' },
              { label: 'Total Users', value: stats.total_users, color: 'purple' },
              { label: 'Total Passkeys', value: stats.total_passkeys, color: 'orange' },
              { label: 'Active Passkeys', value: stats.active_passkeys, color: 'emerald' },
              { label: 'Used Passkeys', value: stats.used_passkeys, color: 'cyan' },
              { label: 'Total RFQs', value: stats.total_rfqs, color: 'indigo' },
              { label: 'Total Customers', value: stats.total_customers, color: 'pink' },
            ].map((stat, i) => (
              <Card key={i} className="p-3 sm:p-4">
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold">{stat.value}</p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">{stat.label}</p>
              </Card>
            ))}
          </div>
        )}

        {/* Passkeys Tab */}
        {activeTab === 'passkeys' && (
          <div className="space-y-4">
            <Card className="p-3 sm:p-4">
              <h3 className="font-bold mb-4 flex items-center gap-2 text-sm sm:text-base">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" /> Create New Passkey
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="text-xs sm:text-sm">Passkey</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={newPasskey}
                        onChange={(e) => setNewPasskey(e.target.value.toUpperCase())}
                        placeholder="XXXX-XXXX-XXXX"
                        className="font-mono text-sm"
                      />
                      <Button variant="outline" onClick={generatePasskey} className="shrink-0 text-xs sm:text-sm">
                        Generate
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs sm:text-sm">Description (Company Name)</Label>
                    <Input
                      value={passkeyDesc}
                      onChange={(e) => setPasskeyDesc(e.target.value)}
                      placeholder="Company name or purpose"
                      className="mt-1 text-sm"
                    />
                  </div>
                </div>
                
                {/* Feature Access Selection */}
                <div className="border rounded-lg p-3 sm:p-4 bg-gray-50">
                  <Label className="text-xs sm:text-sm font-semibold mb-3 block">Module Access</Label>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                    {[
                      { key: 'costing', label: 'Costing' },
                      { key: 'rfq', label: 'RFQ' },
                      { key: 'customers', label: 'Customers' },
                      { key: 'reports', label: 'Reports' },
                    ].map(feature => (
                      <label 
                        key={feature.key}
                        className={`flex items-center gap-2 p-2 sm:p-3 rounded-lg border cursor-pointer transition-all text-xs sm:text-sm ${
                          passkeyFeatures[feature.key] 
                            ? 'bg-green-50 border-green-300' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={passkeyFeatures[feature.key]}
                          onChange={(e) => setPasskeyFeatures(prev => ({
                            ...prev,
                            [feature.key]: e.target.checked
                          }))}
                          className="w-4 h-4 rounded"
                        />
                        <span className="font-medium">{feature.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button onClick={createPasskey} disabled={loading} className="bg-green-600 hover:bg-green-700 text-sm">
                    <Plus className="w-4 h-4 mr-2" /> Create Passkey
                  </Button>
                </div>
              </div>
            </Card>

            <Card className="p-3 sm:p-4">
              <h3 className="font-bold mb-4 text-sm sm:text-base">All Passkeys ({passkeys.length})</h3>
              
              {/* Mobile Cards View */}
              <div className="lg:hidden space-y-3">
                {passkeys.map((pk, i) => (
                  <div key={i} className="border rounded-lg p-3 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{pk.passkey}</code>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        pk.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {pk.status}
                      </span>
                    </div>
                    {pk.description && <p className="text-sm text-gray-600 mb-2">{pk.description}</p>}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {pk.features?.costing && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">Costing</span>}
                      {pk.features?.rfq && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px]">RFQ</span>}
                      {pk.features?.customers && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px]">Customers</span>}
                      {pk.features?.reports && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px]">Reports</span>}
                    </div>
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>{pk.used_by ? `Used by: ${pk.used_by}` : 'Not used'}</span>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => togglePasskey(pk.passkey, pk.status)} className="h-8 w-8 p-0">
                          {pk.status === 'active' ? <Ban className="w-3 h-3 text-red-600" /> : <CheckCircle className="w-3 h-3 text-green-600" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deletePasskey(pk.passkey)} className="h-8 w-8 p-0">
                          <Trash2 className="w-3 h-3 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Passkey</th>
                      <th className="text-left p-2">Description</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Features</th>
                      <th className="text-left p-2">Used By</th>
                      <th className="text-left p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passkeys.map((pk, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-mono text-xs">{pk.passkey}</td>
                        <td className="p-2">{pk.description || '-'}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            pk.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>{pk.status}</span>
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {pk.features?.costing && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Costing</span>}
                            {pk.features?.rfq && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">RFQ</span>}
                            {pk.features?.customers && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">Customers</span>}
                            {pk.features?.reports && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">Reports</span>}
                          </div>
                        </td>
                        <td className="p-2">{pk.used_by || '-'}</td>
                        <td className="p-2">
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => togglePasskey(pk.passkey, pk.status)}>
                              {pk.status === 'active' ? <Ban className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-green-600" />}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deletePasskey(pk.passkey)}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Tenants Tab */}
        {activeTab === 'tenants' && (
          <Card className="p-3 sm:p-4">
            <h3 className="font-bold mb-4 text-sm sm:text-base">All Tenants ({tenants.length})</h3>
            
            {/* Mobile Cards */}
            <div className="lg:hidden space-y-3">
              {tenants.map((t, i) => (
                <div key={i} className="border rounded-lg p-3 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-sm">{t.company_name}</p>
                      <code className="text-xs bg-gray-100 px-1 rounded">{t.company_code}</code>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      t.status === 'active' || !t.status ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>{t.status || 'active'}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{t.contact_person} • {t.contact_email}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleTenant(t.company_code, t.status || 'active')}
                    className="w-full text-xs"
                  >
                    {t.status === 'active' || !t.status ? 'Disable Tenant' : 'Enable Tenant'}
                  </Button>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Code</th>
                    <th className="text-left p-2">Company Name</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Contact</th>
                    <th className="text-left p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-mono">{t.company_code}</td>
                      <td className="p-2">{t.company_name}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          t.status === 'active' || !t.status ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>{t.status || 'active'}</span>
                      </td>
                      <td className="p-2 text-xs">{t.contact_person}<br/>{t.contact_email}</td>
                      <td className="p-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleTenant(t.company_code, t.status || 'active')}
                        >
                          {t.status === 'active' || !t.status ? 'Disable' : 'Enable'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card className="p-3 sm:p-4">
            <h3 className="font-bold mb-4 text-sm sm:text-base">All Users ({users.length})</h3>
            
            {/* Mobile Cards */}
            <div className="lg:hidden space-y-3">
              {users.map((u, i) => (
                <div key={i} className="border rounded-lg p-3 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-semibold text-sm">{u.username}</p>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      u.role === 'master' ? 'bg-purple-100 text-purple-700' :
                      u.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{u.role}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{u.company_code || 'System'}</span>
                    <span className={`px-2 py-0.5 rounded-full ${
                      u.status === 'disabled' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>{u.status || 'active'}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Username</th>
                    <th className="text-left p-2">Role</th>
                    <th className="text-left p-2">Company</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Passkey</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-medium">{u.username}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          u.role === 'master' ? 'bg-purple-100 text-purple-700' :
                          u.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{u.role}</span>
                      </td>
                      <td className="p-2">{u.company_code || '-'}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          u.status === 'disabled' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        }`}>{u.status || 'active'}</span>
                      </td>
                      <td className="p-2 font-mono text-xs">{u.passkey || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Backup Tab */}
        {activeTab === 'backup' && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="p-4 sm:p-6">
              <div className="text-center">
                <Download className="w-10 h-10 sm:w-12 sm:h-12 text-blue-500 mx-auto mb-4" />
                <h3 className="font-bold mb-2 text-sm sm:text-base">Download Backup</h3>
                <p className="text-xs sm:text-sm text-gray-500 mb-4">Download complete database backup as JSON</p>
                <Button onClick={downloadBackup} className="bg-blue-600 hover:bg-blue-700 text-sm">
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </div>
            </Card>

            <Card className="p-4 sm:p-6">
              <div className="text-center">
                <Upload className="w-10 h-10 sm:w-12 sm:h-12 text-green-500 mx-auto mb-4" />
                <h3 className="font-bold mb-2 text-sm sm:text-base">Restore Backup</h3>
                <p className="text-xs sm:text-sm text-gray-500 mb-4">Restore data from a backup file</p>
                <label className="inline-block">
                  <input type="file" accept=".json" onChange={handleRestore} className="hidden" />
                  <Button as="span" className="bg-green-600 hover:bg-green-700 cursor-pointer text-sm">
                    <Upload className="w-4 h-4 mr-2" /> Upload & Restore
                  </Button>
                </label>
              </div>
            </Card>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <Card className="p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-sm sm:text-base">Activity Logs</h3>
              <Button size="sm" variant="outline" onClick={fetchLogs}>
                <RefreshCw className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {logs.map((log, i) => (
                <div key={i} className="p-2 sm:p-3 bg-gray-50 rounded-lg text-xs sm:text-sm">
                  <div className="flex justify-between flex-wrap gap-1">
                    <span className="font-medium">{log.action}</span>
                    <span className="text-gray-500">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-gray-600 mt-1">{log.details}</p>
                </div>
              ))}
              {logs.length === 0 && <p className="text-center text-gray-500 py-8">No activity logs</p>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
